import React, { useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent, Node, mergeAttributes } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';

import EditorToolbar from './EditorToolbar';
import './RichTextEditor.scss';

// Minimal video node — Tiptap doesn't ship one, so we wire a block-level
// <figure class="news-video"><video><figcaption></figcaption></figure>.
// The figcaption is optional; when empty the renderer omits it so older
// posts without a caption stay clean.
const Video = Node.create({
  name: 'video',
  group: 'block',
  selectable: true,
  draggable: true,
  atom: true,

  addAttributes() {
    return {
      src: { default: null },
      caption: { default: '' },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'figure.news-video',
        getAttrs: (node) => {
          const video = node.querySelector('video');
          const cap = node.querySelector('figcaption');
          return {
            src: video?.getAttribute('src') || null,
            caption: cap?.textContent || '',
          };
        },
      },
      // Backwards-compat for older posts that stored a bare <video>.
      {
        tag: 'video[src]',
        getAttrs: (node) => ({
          src: node.getAttribute('src'),
          caption: '',
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { src, caption } = HTMLAttributes;
    const children = [
      [
        'video',
        {
          src,
          controls: 'controls',
          preload: 'metadata',
          playsinline: 'true',
        },
      ],
    ];
    if (caption) {
      children.push(['figcaption', {}, caption]);
    }
    return [
      'figure',
      mergeAttributes({}, { class: 'news-video' }),
      ...children,
    ];
  },

  addCommands() {
    return {
      setVideo:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    };
  },
});

// RichTextEditor wraps Tiptap with the extension set we need across the app
// (currently news, possibly help-pages later). Image insertion delegates to
// the caller via `onUploadImage` so the same editor can talk to different
// upload pipelines (news, help, etc.).
//
// Props:
//   - value: string         current HTML
//   - onChange: (html)      called on every change (debounced by Tiptap)
//   - placeholder: string   shown when empty
//   - onUploadImage: async (file) => imageUrl
//                           if provided, the toolbar's image button uses it;
//                           otherwise the toolbar prompts for a URL.
//   - onUploadVideo: async (file) => { src, poster? }
//                           when provided the toolbar shows a video upload
//                           button that inserts a <video> node on resolve.
const RichTextEditor = ({ value, onChange, placeholder, onUploadImage, onUploadVideo }) => {
  // Keep the latest onChange in a ref so we don't have to recreate the
  // editor when the parent re-renders with a new function identity.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, autolink: true }),
      Image.configure({ inline: false, allowBase64: false }),
      Video,
      Placeholder.configure({ placeholder: placeholder || 'Inhalt eingeben…' }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || '',
    onUpdate: ({ editor: e }) => {
      onChangeRef.current?.(e.getHTML());
    },
  });

  // Sync external value changes (e.g. when loading an existing news entry
  // after the editor has already mounted with empty content).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || '';
    if (current !== next) {
      editor.commands.setContent(next, false);
    }
  }, [editor, value]);

  const insertImage = useCallback(
    async (file) => {
      if (!editor) return;
      if (onUploadImage) {
        const url = await onUploadImage(file);
        if (url) editor.chain().focus().setImage({ src: url }).run();
      }
    },
    [editor, onUploadImage],
  );

  const insertImageByUrl = useCallback(
    (url) => {
      if (!editor || !url) return;
      editor.chain().focus().setImage({ src: url }).run();
    },
    [editor],
  );

  const insertVideo = useCallback(
    async (file, caption) => {
      if (!editor || !onUploadVideo) return;
      const result = await onUploadVideo(file);
      if (!result?.src) return;
      editor
        .chain()
        .focus()
        .setVideo({ src: result.src, caption: caption || '' })
        .run();
    },
    [editor, onUploadVideo],
  );

  return (
    <div className="rich-text-editor">
      <EditorToolbar
        editor={editor}
        onUploadImage={onUploadImage ? insertImage : null}
        onInsertImageByUrl={insertImageByUrl}
        onUploadVideo={onUploadVideo ? insertVideo : null}
      />
      <EditorContent editor={editor} className="rich-text-editor__content" />
    </div>
  );
};

export default RichTextEditor;
